import { money, minutes, type BaselineBudgetSnapshot, type BudgetAssignment, type Money, type Minutes, type Resource } from '@bigint-project/core';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface ProjectBudgetDialogProps {
  readonly isOpen: boolean;
  /** Non-budget resources are ignored defensively; callers may pass the whole pool. */
  readonly resources: readonly Resource[];
  readonly existingBudgetAssignments: readonly BudgetAssignment[];
  readonly currency: string;
  readonly baselineSnapshot?: readonly BaselineBudgetSnapshot[];
  readonly currentTotalCost: Money;
  readonly currentTotalWork: Minutes;
  readonly onSave: (nextAssignments: readonly BudgetAssignment[]) => void;
  readonly onCancel: () => void;
}

type DraftValues = Record<string, string>;

function formatMoney(value: number, currency: string): string {
  try {
    return value.toLocaleString('zh-TW', { style: 'currency', currency, maximumFractionDigits: 0 });
  } catch {
    return `${currency} ${value.toLocaleString('zh-TW', { maximumFractionDigits: 0 })}`;
  }
}

function formatHours(minutesValue: number): string {
  const hours = minutesValue / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

function parseNonnegative(value: string): number | null {
  if (value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function makeDraft(resources: readonly Resource[], assignments: readonly BudgetAssignment[]): DraftValues {
  const assignmentByResource = new Map(assignments.map((assignment) => [assignment.resourceId as string, assignment]));
  return Object.fromEntries(resources.filter((resource) => resource.isBudget).map((resource) => {
    const assignment = assignmentByResource.get(resource.id as string);
    const value = resource.type === 'Cost'
      ? assignment?.budgetCost as number | undefined
      : assignment?.budgetWork as number | undefined;
    return [resource.id as string, value === undefined ? '' : String(resource.type === 'Cost' ? value : value / 60)];
  }));
}

/** Controlled project-summary budget editor; it deliberately has no store or scheduling dependency. */
export function ProjectBudgetDialog({
  isOpen,
  resources,
  existingBudgetAssignments,
  currency,
  baselineSnapshot,
  currentTotalCost,
  currentTotalWork,
  onSave,
  onCancel,
}: ProjectBudgetDialogProps): React.JSX.Element | null {
  const [draft, setDraft] = useState<DraftValues>({});
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const budgetResources = useMemo(() => resources.filter((resource) => resource.isBudget), [resources]);
  const baselineByResource = useMemo(
    () => new Map((baselineSnapshot ?? []).map((snapshot) => [snapshot.resourceId as string, snapshot])),
    [baselineSnapshot],
  );

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setDraft(makeDraft(resources, existingBudgetAssignments));
      setError(null);
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      firstInputRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [existingBudgetAssignments, isOpen, resources]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const budgetCost = budgetResources.reduce((total, resource) => total + (resource.type === 'Cost' ? (parseNonnegative(draft[resource.id as string] ?? '') ?? 0) : 0), 0);
  const budgetWork = budgetResources.reduce((total, resource) => total + (resource.type === 'Cost' ? 0 : (parseNonnegative(draft[resource.id as string] ?? '') ?? 0) * 60), 0);
  const hasBudgetCost = budgetResources.some((resource) => resource.type === 'Cost' && (draft[resource.id as string] ?? '').trim() !== '');
  const hasBudgetWork = budgetResources.some((resource) => resource.type !== 'Cost' && (draft[resource.id as string] ?? '').trim() !== '');

  const save = (): void => {
    const existingByResource = new Map(existingBudgetAssignments.map((assignment) => [assignment.resourceId as string, assignment]));
    const next: BudgetAssignment[] = [];
    for (const resource of budgetResources) {
      const value = parseNonnegative(draft[resource.id as string] ?? '');
      if (value === null) {
        if ((draft[resource.id as string] ?? '').trim() === '') continue;
        setError(`${resource.name} 的預算必須是有限的非負數。`);
        return;
      }
      const budgetWorkMinutes = value * 60;
      if (resource.type !== 'Cost' && !Number.isFinite(budgetWorkMinutes)) {
        setError(`${resource.name} 的預算工時換算後必須是有限數值。`);
        return;
      }
      const existing = existingByResource.get(resource.id as string);
      next.push({
        id: existing?.id ?? `budget-${resource.id as string}`,
        resourceId: resource.id,
        ...(resource.type === 'Cost' ? { budgetCost: money(value) } : { budgetWork: minutes(budgetWorkMinutes) }),
      });
    }
    onSave(next);
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} onKeyDown={trapFocus} className="max-h-full w-full max-w-3xl overflow-auto rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4">
          <div><h2 id={titleId} className="text-lg font-semibold">專案預算</h2><p id={descriptionId} className="mt-1 text-sm text-slate-600 dark:text-slate-300">預算資源只屬於專案摘要，不會加入任務排程、實際成本或 EVM。</p></div>
          <button type="button" aria-label="關閉專案預算" onClick={onCancel} className="rounded px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800">×</button>
        </div>

        <section aria-label="預算比較" className="mt-4 grid gap-3 sm:grid-cols-2">
          <Metric label="目前預估成本" value={formatMoney(currentTotalCost as number, currency)} />
          <Metric label="預算成本／差異" value={hasBudgetCost ? `${formatMoney(budgetCost, currency)} ／ ${formatMoney((currentTotalCost as number) - budgetCost, currency)}` : '—'} />
          <Metric label="目前預估工時" value={formatHours(currentTotalWork as number)} />
          <Metric label="預算工時／差異" value={hasBudgetWork ? `${formatHours(budgetWork)} ／ ${formatHours((currentTotalWork as number) - budgetWork)}` : '—'} />
        </section>

        {budgetResources.length === 0 ? <p className="mt-5 rounded border border-dashed border-slate-300 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">尚無預算資源。請先在資源工作表建立並標示預算資源。</p> : <div className="mt-5 overflow-x-auto"><table className="w-full min-w-[620px] text-left text-sm"><thead><tr className="border-b border-slate-200 dark:border-slate-700"><th className="px-2 py-2">資源</th><th className="px-2 py-2">類型</th><th className="px-2 py-2">專案預算</th><th className="px-2 py-2">Baseline 0</th></tr></thead><tbody>{budgetResources.map((resource, index) => {
          const isCost = resource.type === 'Cost';
          const baseline = baselineByResource.get(resource.id as string);
          const baselineValue = isCost ? baseline?.budgetCost : baseline?.budgetWork;
          const unit = isCost ? '成本' : '工時（小時）';
          return <tr key={resource.id} className="border-b border-slate-100 dark:border-slate-800"><td className="px-2 py-2 font-medium">{resource.name}</td><td className="px-2 py-2">{isCost ? 'Cost' : resource.type}</td><td className="px-2 py-2"><label className="sr-only" htmlFor={`project-budget-${resource.id as string}`}>{resource.name} 的預算{unit}</label><input ref={index === 0 ? firstInputRef : undefined} id={`project-budget-${resource.id as string}`} type="number" min="0" step="any" value={draft[resource.id as string] ?? ''} onChange={(event) => { setDraft((current) => ({ ...current, [resource.id as string]: event.target.value })); setError(null); }} className="w-full rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></td><td className="px-2 py-2 text-slate-600 dark:text-slate-300">{baselineValue === undefined ? '—' : isCost ? formatMoney(baselineValue as number, currency) : formatHours(baselineValue as number)}</td></tr>;
        })}</tbody></table></div>}
        {error ? <p role="alert" className="mt-3 text-sm text-red-600">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium dark:border-slate-600">取消</button><button type="button" disabled={budgetResources.length === 0} onClick={save} className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50">儲存專案預算</button></div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { readonly label: string; readonly value: string }): React.JSX.Element {
  return <div className="rounded border border-slate-200 p-3 dark:border-slate-700"><p className="text-xs text-slate-500 dark:text-slate-400">{label}</p><p className="mt-1 font-medium">{value}</p></div>;
}

export default ProjectBudgetDialog;
